import { createTheme, Theme } from '@mui/material/styles'

/**
 * Densidad compacta para formularios largos dentro de un modal.
 *
 * Se aplica como tema anidado (ver el prop `compact` del Modal), de modo que
 * baja la escala tipografica y pone los controles en `size='small'` SOLO para
 * el subarbol del dialogo. No usa `zoom` ni transformaciones: el navegador
 * sigue maquetando en pixeles reales, los portales (menus, tooltips, popper)
 * heredan la misma escala y el zoom del usuario sigue funcionando como
 * corresponde.
 */
const COMPACT_FACTOR = 0.85

/**
 * Piso de legibilidad. Sin esto una `caption` de 0.75rem terminaria en 9.6px,
 * que es demasiado chico para texto de ayuda. Preferimos que la escala se
 * aplane abajo antes que volver ilegible la letra chica.
 */
const MIN_FONT_REM = 0.72

/**
 * Achicar solo el `fontSize` deja el texto chico con el aire de un texto
 * grande: MUI define `caption` con lineHeight 1.66 y `body2` con 1.43, que a
 * escala reducida se ven despegados. El interlineado tambien baja, con piso
 * para que las descripciones de varias lineas no se amontonen.
 */
const MIN_LINE_HEIGHT = 1.25

const scaleFontSize = (value: unknown, factor: number): unknown => {
  if (typeof value !== 'string') {
    return typeof value === 'number' ? value * factor : value
  }

  const rem = /^([\d.]+)rem$/.exec(value)
  if (rem) {
    const scaled = Math.max(parseFloat(rem[1]) * factor, MIN_FONT_REM)
    return `${+scaled.toFixed(4)}rem`
  }

  const px = /^([\d.]+)px$/.exec(value)
  if (px) return `${+(parseFloat(px[1]) * factor).toFixed(2)}px`

  return value
}

/**
 * `responsiveFontSizes` (que el tema de la app aplica) deja el `fontSize`
 * repetido dentro de media queries para toda variante mayor a 1rem. Si solo se
 * pisa el `fontSize` de primer nivel, la media query gana y el override no se
 * ve — por eso hay que bajar recursivamente.
 */
const scaleVariant = (variant: unknown, factor: number): unknown => {
  if (!variant || typeof variant !== 'object') return variant

  const scaled: Record<string, unknown> = { ...(variant as object) }

  Object.entries(variant as Record<string, unknown>).forEach(([key, value]) => {
    if (key === 'fontSize') {
      scaled[key] = scaleFontSize(value, factor)
    } else if (key === 'lineHeight' && typeof value === 'number') {
      scaled[key] = +Math.max(value * factor, MIN_LINE_HEIGHT).toFixed(3)
    } else if (key.startsWith('@media')) {
      scaled[key] = scaleVariant(value, factor)
    }
  })

  return scaled
}

const SCALED_VARIANTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'caption',
  'overline'
] as const

const SMALL_BY_DEFAULT = [
  'MuiTextField',
  'MuiFormControl',
  'MuiButton',
  'MuiIconButton',
  'MuiCheckbox',
  'MuiRadio',
  'MuiSwitch',
  'MuiChip',
  'MuiSlider',
  'MuiTable',
  'MuiAutocomplete'
] as const

export const buildCompactTheme = (
  base: Theme,
  factor: number = COMPACT_FACTOR
): Theme => {
  const typography = { ...base.typography } as Record<string, unknown>

  SCALED_VARIANTS.forEach((variant) => {
    if (typography[variant]) {
      typography[variant] = scaleVariant(typography[variant], factor)
    }
  })

  const components = SMALL_BY_DEFAULT.reduce<Record<string, unknown>>(
    (acc, name) => {
      acc[name] = { defaultProps: { size: 'small' } }
      return acc
    },
    {
      // Los iconos de MUI vienen en px fijos: no los alcanza la escala
      // tipografica, hay que bajarlos aparte.
      MuiSvgIcon: {
        styleOverrides: {
          fontSizeSmall: { fontSize: `${1.25 * factor}rem` },
          fontSizeMedium: { fontSize: `${1.5 * factor}rem` }
        }
      }
    }
  )

  // El segundo argumento se fusiona por clave sobre el tema ya construido, asi
  // que conserva paleta, breakpoints y el resto del tema de la app.
  return createTheme(base, { typography, components })
}
